import { lazy, Suspense, useEffect } from "react";
import ErrorBoundary from "@/components/ErrorBoundary";
import { initOneSignal } from "@/lib/notifications";
import { initFirebase } from "@/lib/analytics";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/i18n";
import { AdProvider } from "@/components/ads/AdContext";
import { PremiumProvider } from "@/contexts/PremiumContext";

// Lazy load pages for code splitting
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Premium = lazy(() => import("./pages/Premium"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const Explore = lazy(() => import("./pages/Explore"));
const AssetLinks = lazy(() => import("./pages/AssetLinks"));

const PageLoader = () => (
  <div className="min-h-screen calm-gradient flex items-center justify-center">
    <div className="animate-pulse text-primary">Loading...</div>
  </div>
);

const queryClient = new QueryClient();

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen calm-gradient flex items-center justify-center">
        <div className="animate-pulse text-primary">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};

const AuthRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen calm-gradient flex items-center justify-center">
        <div className="animate-pulse text-primary">Loading...</div>
      </div>
    );
  }

  // Allow authenticated users through when completing a password reset flow.
  // Supabase auto-creates a session when landing on the recovery link, so we
  // must NOT redirect away or the ResetPasswordForm never renders.
  const isPasswordRecovery =
    typeof window !== "undefined" &&
    (new URLSearchParams(window.location.search).get("reset") === "true" ||
      window.location.hash.includes("type=recovery"));

  if (user && !isPasswordRecovery) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const AppInit = () => {
  useEffect(() => {
    initOneSignal();
    initFirebase();
  }, []);
  return null;
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AppInit />
      <LanguageProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <AuthProvider>
                <PremiumProvider>
                <AdProvider>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route
                        path="/"
                        element={<Index />}
                      />
                      <Route
                        path="/auth"
                        element={
                          <AuthRoute>
                            <Auth />
                          </AuthRoute>
                        }
                      />
                      <Route
                        path="/admin"
                        element={
                          <ProtectedRoute>
                            <Admin />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/premium"
                        element={
                          <ProtectedRoute>
                            <Premium />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/payment-success"
                        element={
                          <ProtectedRoute>
                            <PaymentSuccess />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/explore"
                        element={<Explore />}
                      />
                      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                      <Route path="/.well-known/assetlinks.json" element={<AssetLinks />} />
                      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </AdProvider>
                </PremiumProvider>
              </AuthProvider>
            </BrowserRouter>
          </TooltipProvider>
        </LanguageProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
